import { sceneGLBLookupKey } from '@context/utils/linked/buildLinkedCatalogId'
import { logAcabado, logAcabadoApi } from '@context/utils/linked/linkedAcabadoChangeLogger'
import { loadGlbCached } from '@utils/sceneObjects/glbLoaderCache'
import { API } from '@utils/queryBuilder'
import type { Store } from '@types'

const acabadosZonasCache = new Map<number, LinkedVariantPrefetchBundle['acabadosZonasExtra']>()

async function fetchAcabadosZonasExtra(
  idUnidades: number,
): Promise<LinkedVariantPrefetchBundle['acabadosZonasExtra']> {
  const cached = acabadosZonasCache.get(idUnidades)
  if (cached !== undefined) return cached

  try {
    logAcabadoApi('acabadosGetUnidadAcabadosInfo', { idUnidades })
    const acabadosInfo = await API().api.acabadosGetUnidadAcabadosInfo(idUnidades)
    logAcabadoApi(
      'acabadosGetUnidadAcabadosInfo',
      { idUnidades },
      acabadosInfo,
      { zonasCount: acabadosInfo?.data?.zonas?.length },
    )

    const extra = acabadosInfo?.data?.zonas
      ? {
          zonas: acabadosInfo.data.zonas as { idAcabadoUnidad: number; zona: string }[],
        }
      : undefined
    acabadosZonasCache.set(idUnidades, extra)
    return extra
  } catch (error) {
    logAcabado('GLB_PREFETCH_ACABADOS_FAIL', { idUnidades, error: String(error) })
    acabadosZonasCache.set(idUnidades, undefined)
    return undefined
  }
}

async function fetchUnidadDataForCatalog(codeKey: string): Promise<Record<string, unknown> | null> {
  try {
    logAcabadoApi('unidadesGetUnidadPost', { codigo: codeKey, catalogoId: codeKey })
    const newGLBData = await API().api.unidadesGetUnidadPost({
      codigo: codeKey,
      date: '2023-05-12',
      catalogoId: codeKey,
    })
    logAcabadoApi('unidadesGetUnidadPost', { codigo: codeKey }, newGLBData)
    return (newGLBData?.data as Record<string, unknown> | undefined) ?? null
  } catch (error) {
    logAcabado('GLB_PREFETCH_UNIDAD_FAIL', { codeKey, error: String(error) })
    return null
  }
}

export type LinkedVariantPrefetchBundle = {
  codeKey: string
  unidadData: Record<string, unknown>
  glb: Awaited<ReturnType<typeof loadGlbCached>>
  acabadosZonasExtra?: { zonas: { idAcabadoUnidad: number; zona: string }[] }
}

function normalizeCatalogCode(catalogId: string): string {
  return sceneGLBLookupKey(catalogId)
}

/**
 * Precarga metadata + GLB + zonas para un conjunto de catalogIDs únicos (en paralelo).
 * Reutiliza sceneGLB y loadGlbCached cuando ya existen.
 */
export async function prefetchLinkedVariantCatalogs(
  catalogIds: string[],
  sceneGLB: Store['sceneGLB'],
): Promise<Map<string, LinkedVariantPrefetchBundle>> {
  const uniqueKeys = [
    ...new Set(catalogIds.map(normalizeCatalogCode).filter(Boolean)),
  ]
  const result = new Map<string, LinkedVariantPrefetchBundle>()

  await Promise.all(
    uniqueKeys.map(async (codeKey) => {
      try {
        const cached = sceneGLB.get(codeKey) as
          | { glb?: unknown; backGLBInfo?: Record<string, unknown> }
          | undefined
        if (cached?.glb && cached?.backGLBInfo) {
          const idUnidades = cached.backGLBInfo.idUnidades as number | undefined
          let acabadosZonasExtra: LinkedVariantPrefetchBundle['acabadosZonasExtra']
          if (idUnidades) {
            acabadosZonasExtra = await fetchAcabadosZonasExtra(idUnidades)
          }
          result.set(codeKey, {
            codeKey,
            unidadData: cached.backGLBInfo,
            glb: cached.glb as LinkedVariantPrefetchBundle['glb'],
            acabadosZonasExtra,
          })
          return
        }

        const unidadData = await fetchUnidadDataForCatalog(codeKey)
        if (!unidadData) return

        const ref = String(unidadData.referenciaBlender ?? '')
        if (!ref) return

        logAcabado('GLB_PREFETCH_LOAD', { codeKey, referenciaBlender: ref })
        const glb = await loadGlbCached(ref)
        logAcabado('GLB_PREFETCH_DONE', { codeKey })

        let acabadosZonasExtra: LinkedVariantPrefetchBundle['acabadosZonasExtra']
        const idUnidades = unidadData.idUnidades as number | undefined
        if (idUnidades) {
          acabadosZonasExtra = await fetchAcabadosZonasExtra(idUnidades)
        }

        result.set(codeKey, {
          codeKey,
          unidadData,
          glb,
          acabadosZonasExtra,
        })
      } catch (error) {
        logAcabado('GLB_PREFETCH_CATALOG_FAIL', { codeKey, error: String(error) })
      }
    }),
  )

  return result
}
